import { readError } from "./apiError";
import { apiFetch } from "./apiFetch";

const THEMES_URL = process.env.REACT_APP_THEMES_URL;
const LOGOS_URL = process.env.REACT_APP_LOGOS_URL;

if (!THEMES_URL || !LOGOS_URL) {
  console.error("Brand API URLs missing — check REACT_APP_THEMES_URL and REACT_APP_LOGOS_URL");
}

// ── Themes ────────────────────────────────────────────────────────────────────
// The server reads the user from the access token, so no email or oid is sent.
// The first argument is kept so existing callers don't change.

export const getThemes = async (oid, { signal } = {}) => {
  const res = await apiFetch(THEMES_URL, { signal });
  if (!res.ok) throw new Error(await readError(res));
  const data = await res.json();
  return Array.isArray(data) ? data : (data?.themes ?? []);
};

export const createTheme = async (oid, plan, { name, background, text, highlight, target }) => {
  const res = await apiFetch(THEMES_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ plan, name, background, text, highlight, target }),
  });
  if (!res.ok) throw new Error(await readError(res));
  return res.json();
};

export const updateTheme = async (oid, theme_id, { name, background, text, highlight, target }) => {
  const res = await apiFetch(THEMES_URL, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ theme_id, name, background, text, highlight, target }),
  });
  if (!res.ok) throw new Error(await readError(res));
  return res.json();
};

export const deleteTheme = async (oid, theme_id) => {
  const res = await apiFetch(THEMES_URL, {
    method: "DELETE",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ theme_id }),
  });
  if (!res.ok) throw new Error(await readError(res));
};

// ── Logos ─────────────────────────────────────────────────────────────────────

// Replacing a logo overwrites the same blob path, so browsers can serve a
// stale copy. created_at changes the URL enough to force a refetch.
const bust = (url, stamp) =>
  url ? `${url}${url.includes("?") ? "&" : "?"}v=${encodeURIComponent(stamp || "")}` : null;

const normaliseLogo = (l) => ({
  ...l,
  logo_color: bust(l.logo_color ?? l.logo_color_url, l.created_at),
  logo_negative: bust(l.logo_negative ?? l.logo_negative_url, l.created_at),
});

export const getLogos = async (oid, { signal } = {}) => {
  const res = await apiFetch(LOGOS_URL, { signal });
  if (!res.ok) throw new Error(await readError(res));
  const data = await res.json();
  const list = Array.isArray(data) ? data : (data?.logos ?? []);
  return list.map(normaliseLogo);
};

export const createLogo = async (oid, plan, target, { logo_color, logo_negative }) => {
  const res = await apiFetch(LOGOS_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ plan, target, logo_color, logo_negative }),
  });
  if (!res.ok) throw new Error(await readError(res));
  return res.json();
};

export const updateLogo = async (oid, target, { logo_color, logo_negative }) => {
  const res = await apiFetch(LOGOS_URL, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ target, logo_color, logo_negative }),
  });
  if (!res.ok) throw new Error(await readError(res));
  return res.json();
};

export const deleteLogo = async (oid, target, which) => {
  const res = await apiFetch(LOGOS_URL, {
    method: "DELETE",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ target, which }),
  });
  if (!res.ok) throw new Error(await readError(res));
};

// ── Helpers ───────────────────────────────────────────────────────────────────

// Only PNG data URLs or https URLs. Blocks data:image/svg+xml and
// javascript:, both of which execute script when used as an img src.
export const isSafeImage = (src) => {
  if (typeof src !== "string") return false;
  if (src.startsWith("data:image/png;base64,")) return true;
  return /^https:\/\//i.test(src);
};

// Converts a File object to base64 data URL for logo upload
export const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

// Maps our internal color keys to the API field names
// canvas → background, structure → text, accent → highlight
export const toApiColors = ({ canvas, structure, accent }) => ({
  background: canvas,
  text: structure,
  highlight: accent,
});

// Maps API response back to our internal keys
export const fromApiColors = ({ background, text, highlight }) => ({
  canvas: background,
  structure: text,
  accent: highlight,
});